import type { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import {
  SortDirection,
  type TableSort,
} from "@redotech/redo-views-common/table";
import {
  rollUpSupplyIntelBrand,
  SUPPLY_INTEL_NO_WAREHOUSE,
  type SupplyIntelBrandRow,
  type SupplyIntelWarehouse,
  supplyIntelWarehouses,
} from "./brand-list";
import {
  rollUpSupplyIntelProducts,
  type SupplyIntelBrandProductRow,
  type SupplyIntelTopProduct,
  supplyIntelTopProducts,
} from "./brand-products";
import {
  type SupplyIntelBrandUnitRow,
  SupplyIntelChannel,
  SupplyIntelSellThroughLever,
} from "./brand-units";
import { supplyIntelCategoryLabel } from "./category";
import type { SupplyIntelConfig } from "./config";
import { listPriceUsd } from "./next-step";
import { SupplyIntelPool } from "./pool";

/** The pool and next-step filter value for units still in process, which have neither. */
export const SUPPLY_INTEL_IN_PROCESS = "in_process";

/** The category filter value for units whose product has no category. */
export const SUPPLY_INTEL_NO_CATEGORY = "none";

/** Where units are, as the dashboard names it: the two held pools are one location. */
export enum SupplyIntelLocation {
  DamagedArea = "damaged_area",
  ForSale = "for_sale",
  Held = "held",
  InProcess = "in_process",
}

export function supplyIntelLocation(
  pool: SupplyIntelPool | null,
): SupplyIntelLocation {
  switch (pool) {
    case SupplyIntelPool.DamagedArea:
      return SupplyIntelLocation.DamagedArea;
    case SupplyIntelPool.ForSale:
      return SupplyIntelLocation.ForSale;
    case SupplyIntelPool.HeldNonSellable:
    case SupplyIntelPool.ReservedInSellableBin:
      return SupplyIntelLocation.Held;
    case null:
      return SupplyIntelLocation.InProcess;
  }
}

export enum SupplyIntelBrandUnitFilterName {
  /** Category paths, or {@link SUPPLY_INTEL_NO_CATEGORY}. */
  Category = "category",
  GradeStatus = "gradeStatus",
  /** Next steps, or {@link SUPPLY_INTEL_IN_PROCESS}. */
  NextStep = "nextStep",
  /** {@link SupplyIntelLocation} values. */
  Pool = "pool",
  /** Warehouse IDs, or {@link SUPPLY_INTEL_NO_WAREHOUSE}. */
  Warehouse = "warehouse",
}

export enum SupplyIntelBrandUnitSortKey {
  Category = "category",
  Grade = "grade",
  ListPrice = "listPrice",
  NextStep = "nextStep",
  OldestAgeDays = "oldestAgeDays",
  Pool = "pool",
  Product = "product",
  Units = "units",
  Warehouse = "warehouse",
}

export const DEFAULT_SUPPLY_INTEL_BRAND_UNIT_SORT: TableSort<SupplyIntelBrandUnitSortKey> =
  { key: SupplyIntelBrandUnitSortKey.Units, direction: SortDirection.DESC };

/** Product row sort keys; the totals keys sort by that total. */
export enum SupplyIntelBrandProductSortKey {
  ActionableValueUsd = "actionableValueUsd",
  KeepOnBrandStoreUnits = "keepOnBrandStoreUnits",
  LikeNewUnits = "likeNewUnits",
  LiquidationFloorUsd = "liquidationFloorUsd",
  OtherUnits = "otherUnits",
  PreOwnedUnits = "preOwnedUnits",
  Product = "product",
  RedoMarketplaceResaleBufferUsd = "redoMarketplaceResaleBufferUsd",
  RedoMarketplaceResaleBufferUsd30 = "redoMarketplaceResaleBufferUsd30",
  RedoMarketplaceResaleBufferUsd50 = "redoMarketplaceResaleBufferUsd50",
  ResaleBufferUsd = "resaleBufferUsd",
  ResaleBufferUsd30 = "resaleBufferUsd30",
  ResaleBufferUsd50 = "resaleBufferUsd50",
  Units = "units",
  Variants = "variants",
}

/** The product sort key holding the resale buffer on each channel at each lever. */
export const SUPPLY_INTEL_PRODUCT_RESALE_BUFFER_SORT_KEY = {
  [SupplyIntelChannel.Ebay]: {
    [SupplyIntelSellThroughLever.Expected]:
      SupplyIntelBrandProductSortKey.ResaleBufferUsd,
    [SupplyIntelSellThroughLever.Thirty]:
      SupplyIntelBrandProductSortKey.ResaleBufferUsd30,
    [SupplyIntelSellThroughLever.Fifty]:
      SupplyIntelBrandProductSortKey.ResaleBufferUsd50,
  },
  [SupplyIntelChannel.RedoMarketplace]: {
    [SupplyIntelSellThroughLever.Expected]:
      SupplyIntelBrandProductSortKey.RedoMarketplaceResaleBufferUsd,
    [SupplyIntelSellThroughLever.Thirty]:
      SupplyIntelBrandProductSortKey.RedoMarketplaceResaleBufferUsd30,
    [SupplyIntelSellThroughLever.Fifty]:
      SupplyIntelBrandProductSortKey.RedoMarketplaceResaleBufferUsd50,
  },
} as const satisfies Record<
  SupplyIntelChannel,
  Record<SupplyIntelSellThroughLever, SupplyIntelBrandProductSortKey>
>;

/** By store value leaving the brand's store, the products to pitch first. */
export const DEFAULT_SUPPLY_INTEL_BRAND_PRODUCT_SORT: TableSort<SupplyIntelBrandProductSortKey> =
  {
    key: SupplyIntelBrandProductSortKey.ActionableValueUsd,
    direction: SortDirection.DESC,
  };

const FILTER_VALUE: Record<
  SupplyIntelBrandUnitFilterName,
  (row: SupplyIntelBrandUnitRow) => string
> = {
  [SupplyIntelBrandUnitFilterName.Category]: (row) =>
    row.category ?? SUPPLY_INTEL_NO_CATEGORY,
  [SupplyIntelBrandUnitFilterName.GradeStatus]: (row) => row.gradeStatus,
  [SupplyIntelBrandUnitFilterName.NextStep]: (row) =>
    row.nextStep ?? SUPPLY_INTEL_IN_PROCESS,
  [SupplyIntelBrandUnitFilterName.Pool]: (row) => supplyIntelLocation(row.pool),
  [SupplyIntelBrandUnitFilterName.Warehouse]: (row) =>
    row.warehouseId ?? SUPPLY_INTEL_NO_WAREHOUSE,
};

function isFilterName(name: string): name is SupplyIntelBrandUnitFilterName {
  return Object.values<string>(SupplyIntelBrandUnitFilterName).includes(name);
}

/** A filter without a value yet (still being edited) matches every row. */
function matchesFilter(
  row: SupplyIntelBrandUnitRow,
  filter: AdvancedFilterData,
): boolean {
  if (
    filter.type !== AdvancedFilterType.ARRAY_TO_STRING ||
    !isFilterName(filter.name) ||
    !filter.value ||
    filter.value.length === 0
  ) {
    return true;
  }
  const matches = filter.value.includes(FILTER_VALUE[filter.name](row));
  if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
    return matches;
  }
  if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
    return !matches;
  }
  return true;
}

/** SKU, else title; what the table shows first for a row. */
export function supplyIntelProductLabel(row: SupplyIntelBrandUnitRow): string {
  return row.sku ?? row.title ?? "";
}

function compareRows(
  a: SupplyIntelBrandUnitRow,
  b: SupplyIntelBrandUnitRow,
  key: SupplyIntelBrandUnitSortKey,
  config: SupplyIntelConfig,
): number {
  switch (key) {
    case SupplyIntelBrandUnitSortKey.Category:
      return supplyIntelCategoryLabel(a.category ?? "").localeCompare(
        supplyIntelCategoryLabel(b.category ?? ""),
      );
    case SupplyIntelBrandUnitSortKey.Grade:
      return (a.grade ?? "").localeCompare(b.grade ?? "");
    case SupplyIntelBrandUnitSortKey.ListPrice:
      // Sorting only: other currencies convert at the config's approximate rates.
      return (listPriceUsd(a, config) ?? -1) - (listPriceUsd(b, config) ?? -1);
    case SupplyIntelBrandUnitSortKey.NextStep:
      return (a.nextStep ?? SUPPLY_INTEL_IN_PROCESS).localeCompare(
        b.nextStep ?? SUPPLY_INTEL_IN_PROCESS,
      );
    case SupplyIntelBrandUnitSortKey.OldestAgeDays:
      return a.oldestAgeDays - b.oldestAgeDays;
    case SupplyIntelBrandUnitSortKey.Pool:
      // By location, as the column shows and filters it. Enum values sort like their labels.
      return supplyIntelLocation(a.pool).localeCompare(
        supplyIntelLocation(b.pool),
      );
    case SupplyIntelBrandUnitSortKey.Product:
      return supplyIntelProductLabel(a).localeCompare(
        supplyIntelProductLabel(b),
      );
    case SupplyIntelBrandUnitSortKey.Units:
      return a.units - b.units;
    case SupplyIntelBrandUnitSortKey.Warehouse:
      return (a.warehouseName ?? "").localeCompare(b.warehouseName ?? "");
  }
}

interface SupplyIntelBrandQuery<SortKey extends string> {
  search?: string;
  advancedFiltersData: readonly AdvancedFilterData[];
  sort: TableSort<SortKey>;
  page: number;
  pageSize: number;
}

export type SupplyIntelBrandUnitQuery =
  SupplyIntelBrandQuery<SupplyIntelBrandUnitSortKey>;
export type SupplyIntelBrandProductQuery =
  SupplyIntelBrandQuery<SupplyIntelBrandProductSortKey>;

export interface SupplyIntelBrandPage<Row> {
  rows: Row[];
  totalCount: number;
  /** The brand's list row, rolled up over the matching rows only. */
  summary: SupplyIntelBrandRow;
  /** The products with the most store value leaving the brand's store, over the matching rows. */
  topProducts: SupplyIntelTopProduct[];
  /** Every warehouse the brand has units in, matching or not, for the warehouse filter. */
  warehouses: SupplyIntelWarehouse[];
  /** Every category path the brand has units in, matching or not, for the category filter. */
  categories: string[];
}

export type SupplyIntelBrandUnitPage =
  SupplyIntelBrandPage<SupplyIntelBrandUnitRow>;
export type SupplyIntelBrandProductPage =
  SupplyIntelBrandPage<SupplyIntelBrandProductRow>;

/** Each category path in the rows, by label. */
function supplyIntelCategories(
  rows: readonly Pick<SupplyIntelBrandUnitRow, "category">[],
): string[] {
  const names = new Set<string>();
  for (const row of rows) {
    if (row.category !== null) {
      names.add(row.category);
    }
  }
  return [...names].sort((a, b) =>
    supplyIntelCategoryLabel(a).localeCompare(supplyIntelCategoryLabel(b)),
  );
}

/** The brand's rows that match the search and filters. */
function matchingRows(
  rows: readonly SupplyIntelBrandUnitRow[],
  query: SupplyIntelBrandQuery<string>,
): SupplyIntelBrandUnitRow[] {
  const search = query.search?.trim().toLowerCase();
  return rows.filter(
    (row) =>
      (!search ||
        [row.sku, row.title, row.productFamilyTitle, row.grade].some((text) =>
          text?.toLowerCase().includes(search),
        ) ||
        row.productId === search) &&
      query.advancedFiltersData.every((filter) => matchesFilter(row, filter)),
  );
}

/** The parts of a page that cover every matching row rather than one page of them. */
function pageContext(
  brand: Pick<SupplyIntelBrandRow, "accountId" | "brandName" | "teamId">,
  rows: readonly SupplyIntelBrandUnitRow[],
  matching: readonly SupplyIntelBrandUnitRow[],
  products: readonly SupplyIntelBrandProductRow[],
  config: SupplyIntelConfig,
) {
  return {
    summary: rollUpSupplyIntelBrand(brand, matching, config),
    topProducts: supplyIntelTopProducts(products),
    warehouses: supplyIntelWarehouses(rows),
    categories: supplyIntelCategories(rows),
  };
}

function pageOf<Row>(
  sorted: readonly Row[],
  query: SupplyIntelBrandQuery<string>,
) {
  const start = query.page * query.pageSize;
  return {
    rows: sorted.slice(start, start + query.pageSize),
    totalCount: sorted.length,
  };
}

/** Search, filter, sort and page one brand's rows; the summary covers every matching row. */
export function querySupplyIntelBrandUnits(
  brand: Pick<SupplyIntelBrandRow, "accountId" | "brandName" | "teamId">,
  rows: readonly SupplyIntelBrandUnitRow[],
  query: SupplyIntelBrandUnitQuery,
  config: SupplyIntelConfig,
): SupplyIntelBrandUnitPage {
  const matching = matchingRows(rows, query);
  const direction = query.sort.direction === SortDirection.ASC ? 1 : -1;
  const sorted = [...matching].sort(
    (a, b) =>
      direction * compareRows(a, b, query.sort.key, config) ||
      supplyIntelProductLabel(a).localeCompare(supplyIntelProductLabel(b)) ||
      a.id.localeCompare(b.id),
  );
  return {
    ...pageOf(sorted, query),
    ...pageContext(
      brand,
      rows,
      matching,
      rollUpSupplyIntelProducts(matching, config),
      config,
    ),
  };
}

function productSortValue(
  product: SupplyIntelBrandProductRow,
  key: Exclude<
    SupplyIntelBrandProductSortKey,
    SupplyIntelBrandProductSortKey.Product
  >,
): number {
  switch (key) {
    case SupplyIntelBrandProductSortKey.LikeNewUnits:
      return product.likeNewResaleUnits + product.likeNewLotUnits;
    case SupplyIntelBrandProductSortKey.PreOwnedUnits:
      return product.preOwnedResaleUnits + product.preOwnedLotUnits;
    case SupplyIntelBrandProductSortKey.OtherUnits:
      return product.otherUnits;
    case SupplyIntelBrandProductSortKey.Variants:
      return product.variants;
    case SupplyIntelBrandProductSortKey.ActionableValueUsd:
    case SupplyIntelBrandProductSortKey.KeepOnBrandStoreUnits:
    case SupplyIntelBrandProductSortKey.LiquidationFloorUsd:
    case SupplyIntelBrandProductSortKey.RedoMarketplaceResaleBufferUsd:
    case SupplyIntelBrandProductSortKey.RedoMarketplaceResaleBufferUsd30:
    case SupplyIntelBrandProductSortKey.RedoMarketplaceResaleBufferUsd50:
    case SupplyIntelBrandProductSortKey.ResaleBufferUsd:
    case SupplyIntelBrandProductSortKey.ResaleBufferUsd30:
    case SupplyIntelBrandProductSortKey.ResaleBufferUsd50:
    case SupplyIntelBrandProductSortKey.Units:
      return product.totals[key];
  }
}

function compareProducts(
  a: SupplyIntelBrandProductRow,
  b: SupplyIntelBrandProductRow,
  key: SupplyIntelBrandProductSortKey,
): number {
  return key === SupplyIntelBrandProductSortKey.Product
    ? (a.title ?? "").localeCompare(b.title ?? "")
    : productSortValue(a, key) - productSortValue(b, key);
}

/** One brand's rows rolled up by store product, then searched, filtered, sorted and paged like the unit rows. */
export function querySupplyIntelBrandProducts(
  brand: Pick<SupplyIntelBrandRow, "accountId" | "brandName" | "teamId">,
  rows: readonly SupplyIntelBrandUnitRow[],
  query: SupplyIntelBrandProductQuery,
  config: SupplyIntelConfig,
): SupplyIntelBrandProductPage {
  const matching = matchingRows(rows, query);
  const products = rollUpSupplyIntelProducts(matching, config);
  const direction = query.sort.direction === SortDirection.ASC ? 1 : -1;
  const sorted = [...products].sort(
    (a, b) =>
      direction * compareProducts(a, b, query.sort.key) ||
      (a.title ?? "").localeCompare(b.title ?? "") ||
      a.id.localeCompare(b.id),
  );
  return {
    ...pageOf(sorted, query),
    ...pageContext(brand, rows, matching, products, config),
  };
}
